import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getAttributeLevelSales } from 'gqlQueries/growthAnalysisQueries';
import { attributeSalesDataParser } from 'parser/attributeSalesDataParser';
import { attributeSalesTableParser } from 'parser/attributeSalestableParser';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
const errorMessage: string = 'Something went wrong!';

function useFetchAttributeLevelSales({ salesTile, selectedTimePeriodDropdownValue }: any) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [comparisonTimePeriod, setComparisonTimePeriod] = useState<any>(null);
    const [count, setCount] = useState(0);
    const variablesRef = useRef<any>({});
    const mergedData = useRef<any[] | null>(null); //using ref here to avoid unnecessary render as this is just used to store data and not display in UI

    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const [
        fetchAttributeLevelSales,
        { data: attributeSalesData, loading: attributeSalesDataLoading, error: attributeSalesDataError },
    ] = useLazyQuery(getAttributeLevelSales, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
    });

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { getGrowthAnalysisData: growthAnalysisAttributeLevelData } = payload;
            const { fromTab = 'sales', volTile } = variablesRef.current;
            let mergedDataRes: any = [];
            //if merged data available and offset is different from default offset value, it means request coming from infinite scroll
            if (
                mergedData.current &&
                Array.isArray(mergedData.current) &&
                mergedData?.current.length > 0 &&
                commonConfig.offset !== variablesRef.current?.offset
            ) {
                const temp = [...mergedData.current];
                //merge total sales, old data, new data
                mergedDataRes = [...temp, ...(growthAnalysisAttributeLevelData?.data?.slice(1) || [])]; //remove total
            } else
                mergedDataRes = [
                    ...(growthAnalysisAttributeLevelData?.data?.slice(growthAnalysisConfig?.showTotal ? 0 : 1) || []),
                ];
            mergedData.current = mergedDataRes;
            if (growthAnalysisAttributeLevelData?.comparisonTimePeriod)
                setComparisonTimePeriod(growthAnalysisAttributeLevelData.comparisonTimePeriod);

            const parsedChartData = attributeSalesDataParser(
                mergedDataRes,
                fromTab,
                undefined,
                undefined,
                undefined,
                growthAnalysisConfig?.showTotal,
            );
            const parsedTableData = attributeSalesTableParser(mergedDataRes, fromTab, volTile, appConfig, undefined, undefined, undefined, selectedTimePeriodDropdownValue);
            setData({ tableData: parsedTableData, chartData: parsedChartData });
            setCount(growthAnalysisAttributeLevelData.meta?.count || 0);
        } catch (error) {
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setComparisonTimePeriod(null);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        count,
        comparisonTimePeriod,
        data: salesTile?.key !== variablesRef.current?.fromTab ? null : data, //avoid sending duplicates
        fetchAttributeLevelSales: (variables: any) => {
            variablesRef.current = variables.variables;
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (commonConfig.offset === variables.variables?.offset) {
                setData(null);
                setError(null);
            }
            setLoading(true);
            fetchAttributeLevelSales(variables);
            // onFetchComplete(variables);
        },
    };
}

export { useFetchAttributeLevelSales };
